'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, ChevronDown } from 'lucide-react';

const privateNavLinks = [
  { label: 'Directory', href: '/directory' },
  { label: 'Conversations', href: '/conversations' },
  { label: 'Services', href: '/services' },
  { label: 'Marketplace', href: '/marketplace' },
  { label: 'Events', href: '/events' },
  { label: 'Newsletter', href: '/newsletter' },
  { label: 'Resources', href: '/resources' },
];

const discoverLinks = [
  { label: 'Why Panorama', href: '/why-panorama' },
  { label: 'Amenities', href: '/amenities' },
  { label: 'Lifestyle', href: '/lifestyle' },
  { label: 'Community', href: '/community' },
  { label: 'Nearby', href: '/nearby' },
  { label: 'Real Estate', href: '/real-estate' },
  { label: 'FAQ', href: '/faq' },
];

interface SiteHeaderProps {
  theme?: 'light' | 'dark' | 'transparent';
}

export default function SiteHeader({ theme = 'light' }: SiteHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handler = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-discover-menu]')) setDiscoverOpen(false);
      if (!target.closest('[data-community-menu]')) setCommunityOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isDark = theme === 'dark' || theme === 'transparent';

  const headerBg = isScrolled
    ? 'backdrop-blur-md border-b'
    : theme === 'transparent' ? 'backdrop-blur-md' : 'backdrop-blur-md border-b';

  const scrolledStyle = isScrolled
    ? {
        background: 'rgba(7,6,12,0.55)',
        borderColor: 'rgba(255,255,255,0.08)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.04)',
      }
    : isDark
    ? {
        background: 'rgba(7,6,12,0.40)',
        borderColor: 'rgba(255,255,255,0.06)',
      }
    : {
        background: 'rgba(7,6,12,0.55)',
        borderColor: 'rgba(255,255,255,0.08)',
      };

  function isActive(href: string) {
    return pathname === href || pathname.startsWith(href + '/');
  }

  const isDiscoverActive = discoverLinks.some((l) => isActive(l.href));
  const isCommunityActive = privateNavLinks.some((l) => isActive(l.href));

  return (
    <>
      {/* Header scrim */}
      {isDark && !isScrolled && (
        <div
          className="fixed top-0 left-0 right-0 z-49 pointer-events-none"
          style={{
            height: '96px',
            background: 'linear-gradient(to bottom, rgba(7,6,12,0.70) 0%, rgba(7,6,12,0.25) 70%, transparent 100%)',
          }}
        />
      )}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
        style={scrolledStyle}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group">
            <AppLogo size={32} />
            <div className="flex flex-col leading-none">
              <span
                className="font-display font-bold text-[15px] tracking-tight transition-colors duration-200"
                style={{ color: '#F2F4FF' }}
              >
                Panorama
              </span>
              <span
                className="text-[10px] font-medium tracking-[0.12em] uppercase transition-colors duration-200"
                style={{ color: 'rgba(167,163,196,0.60)' }}
              >
                Towers
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {/* Discover dropdown */}
            <div className="relative" data-discover-menu>
              <button
                onClick={() => { setDiscoverOpen(!discoverOpen); setCommunityOpen(false); }}
                className="nav-link px-3.5 py-2 text-[13.5px] font-medium rounded-md transition-all duration-150 hover:bg-white/[0.06] flex items-center gap-1.5 relative"
                style={isDiscoverActive ? { color: '#4CC9FF', background: 'rgba(76,201,255,0.08)' } : {}}
              >
                Discover
                <ChevronDown size={13} className={`transition-transform duration-200 ${discoverOpen ? 'rotate-180' : ''}`} />
                {isDiscoverActive && (
                  <span
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                    style={{ background: '#4CC9FF' }}
                  />
                )}
              </button>
              {discoverOpen && (
                <div
                  className="absolute top-full left-0 mt-1 w-48 py-1.5 shadow-2xl"
                  style={{
                    background: '#0C0A16',
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: '10px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.60)',
                  }}
                >
                  {discoverLinks.map((link) => {
                    const active = isActive(link.href);
                    return (
                      <Link
                        key={`discover-${link.label}`}
                        href={link.href}
                        onClick={() => setDiscoverOpen(false)}
                        className="flex items-center px-3.5 py-2.5 text-[13px] font-medium transition-all duration-150 hover:bg-white/[0.06]"
                        style={active ? { color: '#4CC9FF' } : { color: '#A7A3C4' }}
                      >
                        {link.label}
                        {active && <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: '#4CC9FF' }} />}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Community dropdown */}
            <div className="relative" data-community-menu>
              <button
                onClick={() => { setCommunityOpen(!communityOpen); setDiscoverOpen(false); }}
                className="nav-link px-3.5 py-2 text-[13.5px] font-medium rounded-md transition-all duration-150 hover:bg-white/[0.06] flex items-center gap-1.5 relative"
                style={isCommunityActive ? { color: '#4CC9FF', background: 'rgba(76,201,255,0.08)' } : {}}
              >
                Community
                <ChevronDown size={13} className={`transition-transform duration-200 ${communityOpen ? 'rotate-180' : ''}`} />
                {isCommunityActive && (
                  <span
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                    style={{ background: '#4CC9FF' }}
                  />
                )}
              </button>
              {communityOpen && (
                <div
                  className="absolute top-full left-0 mt-1 w-48 py-1.5 shadow-2xl"
                  style={{
                    background: '#0C0A16',
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: '10px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.60)',
                  }}
                >
                  {privateNavLinks.map((link) => {
                    const active = isActive(link.href);
                    return (
                      <Link
                        key={`community-${link.label}`}
                        href={link.href}
                        onClick={() => setCommunityOpen(false)}
                        className="flex items-center px-3.5 py-2.5 text-[13px] font-medium transition-all duration-150 hover:bg-white/[0.06]"
                        style={active ? { color: '#4CC9FF' } : { color: '#A7A3C4' }}
                      >
                        {link.label}
                        {active && <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: '#4CC9FF' }} />}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="/verification/pending"
              className="text-[13px] font-medium transition-colors duration-150 hover:opacity-80 px-3 py-2"
              style={{ color: '#A7A3C4' }}
            >
              Sign In
            </Link>
            <Link
              href="/request-access"
              className="relative text-[#F2F4FF] text-[13px] font-semibold px-4 py-2 transition-all duration-200 active:scale-95 hover:shadow-neon-btn"
              style={{
                borderRadius: '8px',
                background: '#0F0D1D',
                border: '1px solid transparent',
                backgroundClip: 'padding-box',
                isolation: 'isolate',
                display: 'inline-block',
              }}
            >
              <span
                className="absolute inset-[-1px] pointer-events-none"
                style={{
                  borderRadius: '9px',
                  background: 'linear-gradient(135deg, #FF3DF5, #7A5CFF, #4CC9FF)',
                  zIndex: -1,
                }}
              />
              Request Access
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-md transition-all duration-150 hover:bg-white/[0.08] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(76,201,255,0.40)]"
            style={{ color: '#F2F4FF' }}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 backdrop-blur-sm"
            style={{ background: 'rgba(7,6,12,0.75)' }}
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="absolute top-0 right-0 h-full w-72 flex flex-col shadow-2xl"
            style={{
              background: '#0C0A16',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '0 0 0 12px',
            }}
          >
            <div
              className="h-16 flex items-center justify-between px-5 border-b"
              style={{ borderColor: 'rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-center gap-2">
                <AppLogo size={28} />
                <span className="font-display font-bold text-[14px]" style={{ color: '#F2F4FF' }}>
                  Panorama Towers
                </span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1.5 rounded-md transition-all duration-150 hover:bg-white/[0.08] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(76,201,255,0.40)]"
                style={{ color: '#A7A3C4' }}
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3">
              {/* Discover section */}
              <div className="mb-2">
                <p className="text-[10px] font-semibold tracking-[0.12em] uppercase px-3 mb-1.5" style={{ color: 'rgba(167,163,196,0.40)' }}>
                  Discover
                </p>
                {discoverLinks.map((link) => {
                  const active = isActive(link.href);
                  return (
                    <Link
                      key={`mobile-discover-${link.label}`}
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="nav-link flex items-center px-3 py-2.5 text-[13.5px] font-medium rounded-lg transition-all duration-150 hover:bg-white/[0.06]"
                      style={active ? { color: '#4CC9FF', background: 'rgba(76,201,255,0.08)' } : {}}
                    >
                      {link.label}
                      {active && <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: '#4CC9FF' }} />}
                    </Link>
                  );
                })}
              </div>

              <div className="h-px mx-3 my-3" style={{ background: 'rgba(255,255,255,0.06)' }} />

              {/* Community section */}
              <div className="mb-2">
                <p className="text-[10px] font-semibold tracking-[0.12em] uppercase px-3 mb-1.5" style={{ color: 'rgba(167,163,196,0.40)' }}>
                  Community
                </p>
                {privateNavLinks.map((link) => {
                  const active = isActive(link.href);
                  return (
                    <Link
                      key={`mobile-nav-${link.label}`}
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="nav-link flex items-center px-3 py-2.5 text-[13.5px] font-medium rounded-lg transition-all duration-150 hover:bg-white/[0.06]"
                      style={active ? { color: '#4CC9FF', background: 'rgba(76,201,255,0.08)' } : {}}
                    >
                      {link.label}
                      {active && <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: '#4CC9FF' }} />}
                    </Link>
                  );
                })}
              </div>

              <div
                className="mt-2 pt-4 border-t px-3 space-y-2"
                style={{ borderColor: 'rgba(255,255,255,0.08)' }}
              >
                <Link
                  href="/verification/pending"
                  onClick={() => setMobileOpen(false)}
                  className="w-full text-[14px] font-medium px-4 py-2.5 rounded-lg transition-all duration-150 hover:bg-white/[0.06] block text-center"
                  style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  Sign In
                </Link>
                <Link
                  href="/request-access"
                  onClick={() => setMobileOpen(false)}
                  className="w-full text-[#F2F4FF] text-[14px] font-semibold px-4 py-3 transition-all duration-150 active:scale-95 relative block text-center"
                  style={{
                    borderRadius: '8px',
                    background: '#0F0D1D',
                    border: '1px solid transparent',
                    isolation: 'isolate',
                  }}
                >
                  <span
                    className="absolute inset-[-1px] pointer-events-none"
                    style={{
                      borderRadius: '9px',
                      background: 'linear-gradient(135deg, #FF3DF5, #7A5CFF, #4CC9FF)',
                      zIndex: -1,
                    }}
                  />
                  Request Access
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}